import { useEffect, useState, useCallback, useContext } from "react";
import { Button, OverlayTrigger, Tooltip, Card, Form, Alert, Spinner } from "react-bootstrap";
import { API } from 'aws-amplify';
import { getAuthToken } from "../../utils/AuthUtils";
import GenericGridComponent from "../../GenericGridComponent/GenericGridComponent";
import { exportToCSV } from "../../utils/exportUtils";
import { ToastContext } from "../../Contexts/ToastProvider";
import "./styles.scss"
import ViewTickersModal from "./ViewTickersModal/ViewTickersModal";

const ClientDataRequestTab = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [error, setError] = useState(null);
    const [errorDisplayStatus, setErrorDisplayStatus] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState("PENDING");
    const [selectedTickerList, setSelectedTickerList] = useState([]);
    const [selectedUserNotes, setSelectedUserNotes] = useState("");
    const [showViewTickerModal, setShowViewTickerModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const { showToast } = useContext(ToastContext);

    const copyTickers = async (tickers, showToast) => {
        const tickerStr = tickers.join(", ");
        await navigator.clipboard.writeText(tickerStr);
        showToast(`Copied ${tickerStr} to clipboard`);
    }

    const handleViewTickers = (tickers, userNotes) => {
        setSelectedTickerList(tickers);
        setSelectedUserNotes(userNotes);
        setShowViewTickerModal(true);
    }

    const handleFullfillRequest = async (row) => {
        setLoading(true);
        setError("");
        setErrorDisplayStatus(false);
        const authToken = await getAuthToken();
        const dataRequestId = row.id;
        const requestStatus = selectedStatus === "PENDING" ? "FULFILLED" : "PENDING";
        const body = {
            requestType: row.requestType,
            requestStatus
        }
        const path = `/client-requests/survey-data/${dataRequestId}`
        API.put("AnnualCompensations", path, {
            headers: {
                'Token': authToken
            },
            body
        }).then((response) => {
            setLoading(false);
            getRequestData(selectedStatus);
        }).catch((error) => {
            if (error?.response?.data?.message) {
                console.log(error.response.data.message);
            } else {
                console.log(error);
                console.log("An error occurred while retrieving client data requsts. Please try again later.");
            }
            setLoading(false);
            setError("An error occurred retrieving client data requsts");
            setErrorDisplayStatus(true);
        })
    }

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleString();
    }

    const actions = (row) => {
        return (
            <div className="action-buttons">
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-edit-${row.id}`}>Copy Tickers</Tooltip>}
                >
                    <span>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="action-btn"
                            onClick={() => copyTickers(row.tickers, showToast)}
                            aria-label="Copy Tickers"
                        >
                            <i className="fa fa-copy" />
                        </Button>
                    </span>
                </OverlayTrigger>
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-view-tickers-${row.id}`}>View Tickers</Tooltip>}
                >
                    <span>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="action-btn"
                            onClick={() => handleViewTickers(row.tickers, row.userNotes)}
                            aria-label="View Tickers"

                        >
                            <i className="fa fa-eye" />
                        </Button>

                    </span>
                </OverlayTrigger>
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-view-tickers-${row.id}`}>Edit Request</Tooltip>}
                >
                    <span>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="action-btn"
                            onClick={() => handleFullfillRequest(row)}
                            aria-label="Edit Request"

                        >
                            <i className="fa fa-edit" />
                        </Button>

                    </span>
                </OverlayTrigger>
            </div>
        )
    }

    const columns = [
        {
            "key": "actions",
            "sortable": false,
            "filterable": false,
            "formatter": (props) => {
                return (
                    actions(props.row)
                )
            }
        }, {
            "key": "clientName",
            "name": "Client Name",
            "sortable": true,
            "filterable": true
        }, {
            "key": "userName",
            "name": "User Name",
            "sortable": true,
            "filterable": true
        }, {
            "key": "email",
            "name": "Primary Email",
            "sortable": true,
            "filterable": true
        }, {
            "key": "tickers",
            "name": "No. companies",
            "sortable": true,
            "filterable": true,
            "formatter": (props) => {
                return props.row.tickers.length;
            }
        }, {
            "key": "requestType",
            "name": "Request Type",
            "sortable": true,
            "filterable": true
        }, {
            "key": "requestStatus",
            "name": "Reqest Status",
            "sortable": true,
            "filterable": true
        }, {
            "key": "createdOn",
            "name": "Created On",
            "sortable": true,
            "filterable": true,
            "formatter": (props) => {
                return formatDate(props.row.createdOn)
            }
        }, {
            "key": "secondaryEmail",
            "name": "Secondary Email",
            "sortable": true,
            "filerable": true
        }
    ]


    const createDataRows = (data) => {
        return data.map(row => {
            return {
                "User Name": row.userName,
                "Client": row.clientName,
                "Primary Email": row.email,
                "Tickers": row.tickers.join(", "),
                "Request Type": row.requestType,
                "Request Status": row.requestStatus,
                "Created On": row.createdOn,
                "Secondary Email": row.secondaryEmail
            }
        });
    }

    const getRequestData = useCallback(async (status) => {
        setLoading(true);
        setError("");
        setErrorDisplayStatus(false);
        const authToken = await getAuthToken();
        const queryStringParameters = {
            requestStatus: status
        }
        const path = "/client-requests/survey-data";
        API.get('AnnualCompensations', path, {
            headers: {
                'Token': authToken
            },
            queryStringParameters
        }).then((response) => {
            setLoading(false);
            setData(response);
        }).catch((error) => {
            if (error?.response?.data?.message) {
                console.log(error.response.data.message);
            } else {
                console.log(error);
                console.log("An error occurred while retrieving client data requsts. Please try again later.");
            }
            setLoading(false);
            setError("An error occurred retrieving client data requsts");
            setErrorDisplayStatus(true);
        })
    }, []);

    useEffect(() => {
        let mounted = true;
        if (mounted) {
            setData([]);
            setLoading(false);
            setErrorDisplayStatus(false);
            setError("");
            getRequestData(selectedStatus);
            setCurrentPage(1)
        }
        return () => mounted = false;
    }, [selectedStatus, getRequestData])


    const renderContent = () => {
        if (loading) {
            return (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Loading submissions...</p>
                </div>
            );
        }

        if (data.length === 0) {
            let friendlyStatusMsg = '';
            switch (selectedStatus) {
                case 'PENDING':
                    friendlyStatusMsg = 'No pending requests found.';
                    break;
                case 'FULFILLED':
                    friendlyStatusMsg = 'No fulfilled reqests found.';
                    break;
                default:
                    friendlyStatusMsg = 'No requests found.';
            }
            return (
                <Alert variant="info" className="text-center">
                    <i className="fa fa-info-circle me-2" />
                    {friendlyStatusMsg}
                </Alert>
            );
        }

        return (
            <GenericGridComponent
                columns={columns}
                data={data}
                enableGridToCSV={true}
                useCustomExportMethod={true}
                customExportMethod={() => exportToCSV(data, createDataRows, "Survey_Submissions", "Survey Submissions")}
                fileName={"Client_Data_Requests"}
                itemsName={"Client Data Requests"}
                selectedCurrentPage={currentPage}
                onCurrentPageSelect={(page) => setCurrentPage(page)}
            />
        );
    }

    return (
        <><ViewTickersModal
            show={showViewTickerModal}
            onClose={() => setShowViewTickerModal(false)}
            tickers={selectedTickerList}
            userNotes={selectedUserNotes}
            handleCopy={() => copyTickers(selectedTickerList, showToast)}
        />
            <div className="client-data-request-container">
                <Card className="client-data-request-card">
                    <Card.Header className="filters row">
                        <div className="d-flex flex-column align-items-start">
                            <Form.Group controlId="requestStatus">
                                <Form.Label>Request Status</Form.Label>
                                <Form.Select
                                    value={selectedStatus}
                                    className="col-md-2"
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                    style={{ width: "12.5rem" }}
                                >
                                    <option value={"PENDING"}>Pending</option>
                                    <option value={"FULFILLED"}>Fulfilled</option>
                                </Form.Select>
                            </Form.Group>
                        </div>
                    </Card.Header>

                    <Card.Body>
                        {errorDisplayStatus && (
                            <Alert variant="danger" dismissible onClose={() => setErrorDisplayStatus(false)}>
                                {error}
                            </Alert>
                        )}
                        {renderContent()}
                    </Card.Body>

                </Card>
            </div></>
    )
}

export default ClientDataRequestTab;